import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { db } from '../../../firebase/firebaseConfig';
import { collection, getDocs, doc, getDoc, updateDoc, query, where, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { trackFirstRepCompleted, trackStreakMilestone, trackRoleLevelUp, trackSrsGradeInferred } from '../../../utils/analytics';
import { computeDayStreak } from '../../../utils/dayStreak';
import { stripComments, normalizeCode, splitCodeIntoLines, splitCodeIntoLinesAsync, simpleCodeProcessing, simpleNormalizeCode, extractCommentHints, extractVariableHintsFromLines } from '../../../utils/codeProcessing.js';
import SpacedRepetition from '../../SRS/SpacedRepetition';
import LevelingSystem, { applySnippetExpGain, MAX_SNIPPET_LEVEL } from '../../SRS/LevelingSystem';
import { useAI } from '../../AI'; // Import the AI wrapper hook
import { usePremium } from '../../../contexts/PremiumContext'; // Import premium context hook
import { useDirectPremiumCheck } from '../../../hooks/useDirectPremiumCheck'; // Import direct premium check hook
import * as roadmapService from '../../../services/roadmapService';
import { awardRoleExpForStudy } from '../../../services/roleProgressService';
import {
  isRoleEligibleCookbookDeck,
  resolveNodeIdFromDeckAndCard,
  resolveTrackIdFromDeckAndCard
} from '../../../services/roleResolutionService';
import {
  computeLineAccuracy,
  isCardInLearningPhase,
  formatSrsScheduleLabel
} from '../../SRS/algorithm';
import {
  AGAIN_SRS_PAYLOAD,
  buildCheckReviewMetrics,
  buildDeckSrsCommitOptions,
  formatCheckRecallFeedback,
  isSrsPracticeOnlyForStudy,
  shouldCommitDeckSrsPayload,
  shouldCommitSrsOnCheck,
  shouldDeferFirstExposureSrs
} from './studySrsWiring';
import {
  cardMatchesProgressRow,
  findProgressForCard,
  statsFromCookbookCard
} from '../../../utils/cookbookCardProgress';
import { isSnippetStarted } from '../../Roadmap/RoadmapList';

/** Merge an SRS progress write result onto a card for index / state display. */
const mergeCardSrsProgress = (card, progressResult) => {
  if (!progressResult || !card) return card;
  return {
    ...card,
    progress: {
      ...(card.progress || {}),
      ...progressResult,
      cardId: progressResult.cardId || card.id
    }
  };
};

const attachDeckProgressToCards = (cardsList, progressRows) =>
  cardsList.map((card) => ({
    ...card,
    progress: card.progress || findProgressForCard(card, progressRows) || null
  }));

/** Deck id for a card — supports cross-cookbook roadmap practice sessions. */
const resolveStudyCardDeckId = (deck, card) => {
  if (card?.deckId) return card.deckId;
  if (typeof deck === 'string') return deck;
  if (deck && typeof deck === 'object') {
    if (deck.deckId) return deck.deckId;
    if (deck.id && !deck.practiceStartedSession) return deck.id;
  }
  return null;
};

function resolveStudyDeckId(deck) {
  if (typeof deck === 'string') return deck;
  if (deck && typeof deck === 'object') {
    return deck.deckId || deck.id || null;
  }
  return null;
}

function initialCardsCacheKey(initialCards) {
  if (!initialCards) return '';
  const list = Array.isArray(initialCards) ? initialCards : [initialCards];
  return list.map((card) => card?.id || '').join(',');
}

/** In test mode, node is marked complete when this fraction of snippets are correct (e.g. 0.7 = 70%). */
const TEST_MODE_PASSING_SCORE = 0.7;

/**
 * useStudyLogic — all study-session state and effects for {@link Study}.
 * Must be called from the Study component (custom hook), not invoked as `StudyLogic(...)`.
 */
const useStudyLogic = ({
  userId,
  deck,
  startCardId,
  cards: initialCards,
  dueOnly = false,
  startedOnly = false,
  isTestMode = false,
  setView,
  previousView,
  onReviewQueueChanged,
  onNodeCompleted,
  roadmapTrackId: roadmapTrackIdProp = null,
  roadmapNodeId: roadmapNodeIdProp = null,
  title,
  disableAiFeatures = false
}) => {
  // Core state
  const [cards, setCards] = useState(initialCards || []);
  const [loading, setLoading] = useState(true);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [error, setError] = useState('');
  const loadedDeckContextRef = useRef({
    deckId: null,
    dueOnly: false,
    startedOnly: false,
    initialCardsKey: ''
  });

  const resolvedDeckId = useMemo(() => resolveStudyDeckId(deck), [deck]);

  // Study mode state
  const [studyMode, setStudyMode] = useState('regular');

  // Regular study mode state
  const [showAnswer, setShowAnswer] = useState(false);
  const [userCode, setUserCode] = useState('');
  const [feedback, setFeedback] = useState('');
  const [hasStartedCoding, setHasStartedCoding] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  // Test mode: per-snippet results for passing score (e.g. 70% to complete node)
  const [testModeSnippetResults, setTestModeSnippetResults] = useState([]);
  // Test mode: show one summary when test is complete (all snippets done or wrong answer) before going back
  const [testCompleteSummary, setTestCompleteSummary] = useState(null);

  // Line-by-line study mode state
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [lineResults, setLineResults] = useState([]);
  const [initiallyWrongLines, setInitiallyWrongLines] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [lines, setLines] = useState([]);
  const [showLine, setShowLine] = useState(false);
  const [checkTimeout, setCheckTimeout] = useState(null);
  const [lineComparison, setLineComparison] = useState(null); // Stores character-by-character comparison
  const [commentHints, setCommentHints] = useState([]); // Comment blocks as hints: { text, linesAfter, startLineIndex }[]
  const [variableHints, setVariableHints] = useState([]); // { name, value, lineIndex }[]

  // Notes state
  const [notes, setNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // AI explanation state
  const [explanation, setExplanation] = useState('');
  const [isExplaining, setIsExplaining] = useState(false);
  /** 'study' = normal flashcard; 'explanation' = AI explanation fills the flashcard area (no modal). */
  const [flashcardViewMode, setFlashcardViewMode] = useState('study');
  const [explanationCode, setExplanationCode] = useState(null); // Store the code being explained
  const [explanationType, setExplanationType] = useState('solution'); // 'solution' or 'user'

  // Experience popup state
  const [showExperiencePopup, setShowExperiencePopup] = useState(false);
  const [experienceData, setExperienceData] = useState({
    snippetExp: 0,
    snippetLeveledUp: false
  });

  // Stats and leveling
  const [stats, setStats] = useState(null);
  const [srs] = useState(new SpacedRepetition(userId));

  const notifyReviewQueueChanged = useCallback(() => {
    if (typeof onReviewQueueChanged === 'function') {
      onReviewQueueChanged();
    }
  }, [onReviewQueueChanged]);
  const [levelingSystem] = useState(new LevelingSystem(userId));

  // Get premium status from both context and direct check
  const { isPremium, features } = usePremium();
  const { isPremiumUser } = useDirectPremiumCheck(userId);

  // Get AI functionality from the useAI hook
  const { explainLine, explainSnippet, loading: aiLoading } = useAI();

  // Refs
  const editorContainerRef = useRef(null);
  const [editorHeight, setEditorHeight] = useState(300);
  const navigationAttempted = useRef(false);
  const hasLoadedSavedPosition = useRef(false);
  /**
   * Per-card attempt counter. Incremented every time the user clicks "Check"
   * (regular mode) or submits a line (line-by-line mode), reset by
   * `resetState` whenever we move to a new card.
   *
   * Read by `updateDailyProgress` to attach an `attempt_count` to the
   * one-shot `first_rep_completed` GA4 event — that lets us spot demo-time
   * friction (e.g. typing tolerance too tight on hello world) without
   * needing a second `rep_failed` event per submission.
   */
  const cardAttemptCountRef = useRef(0);
  /** First regular-mode Check commits SRS after first-exposure orientation is recorded. */
  const srsReviewCommittedRef = useRef(false);
  const srsReviewResultRef = useRef(null);

  // Initialize component
  useEffect(() => {
    if (setView) {
      setView('study');
    }

    const init = async () => {
      const initialCardsKey = initialCardsCacheKey(initialCards);
      const loaded = loadedDeckContextRef.current;
      const cardsAlreadyLoaded =
        cards.length > 0 &&
        loaded.deckId === resolvedDeckId &&
        loaded.dueOnly === dueOnly &&
        loaded.startedOnly === startedOnly &&
        loaded.initialCardsKey === initialCardsKey;

      if (cardsAlreadyLoaded) {
        setLoading(false);
        return;
      }

      setLoading(true);
      console.log('StudyLogic: Initializing with:', {
        initialCards: initialCards?.length || 0,
        hasDeck: !!deck,
        userId
      });

      if (initialCards) {
        const cardsArray = Array.isArray(initialCards) ? initialCards : [initialCards];
        console.log('StudyLogic: Processing cards:', cardsArray.map(c => ({
          id: c.id,
          title: c.title,
          hasLevel: c.level !== undefined,
          level: c.level,
          current_exp: c.current_exp
        })));

        const processedCards = await Promise.all(cardsArray.map(async (card) => {
          if (card.level !== undefined) {
            console.log(`StudyLogic: Card ${card.id} already has leveling fields`);
            return card;
          }
          try {
            console.log(`StudyLogic: Initializing leveling for card ${card.id}`);
            const updatedCard = await initializeSnippetLeveling(card.id, card);
            console.log(`StudyLogic: Updated card ${card.id} with leveling:`, {
              level: updatedCard.level,
              current_exp: updatedCard.current_exp,
              exp_to_level: updatedCard.exp_to_level
            });
            return updatedCard;
          } catch (error) {
            console.error('Error initializing leveling fields:', error);
            return card;
          }
        }));

        let cardsWithProgress = processedCards;
        if (userId && deck && !deck.practiceStartedSession) {
          try {
            const deckId = resolveStudyCardDeckId(deck, processedCards[0]);
            if (deckId) {
              const progressRows = await srs.getDeckCardProgressList(deckId);
              cardsWithProgress = attachDeckProgressToCards(processedCards, progressRows);
            }
          } catch (progressError) {
            console.error('StudyLogic: failed to load card progress for index:', progressError);
          }
        }

        console.log('StudyLogic: Final processed cards:', cardsWithProgress.map(c => ({
          id: c.id,
          title: c.title,
          level: c.level,
          current_exp: c.current_exp,
          exp_to_level: c.exp_to_level
        })));

        let targetCardIndex = 0;
        if (startCardId) {
          const foundIndex = cardsWithProgress.findIndex(
            (card) => card.id === startCardId || card.roadmapSnippetId === startCardId
          );
          if (foundIndex !== -1) {
            targetCardIndex = foundIndex;
          }
        }

        setCards(cardsWithProgress);
        setCurrentCardIndex(targetCardIndex);
        resetState();
      } else if (userId && deck) {
        // Load deck and cards, passing startCardId if provided
        console.log('StudyLogic init: Loading deck with startCardId:', startCardId);
        await loadDeckAndCards(startCardId);
      }
      loadedDeckContextRef.current = {
        deckId: resolvedDeckId,
        dueOnly,
        startedOnly,
        initialCardsKey
      };
      setLoading(false);
    };
    init();
  }, [userId, resolvedDeckId, initialCards, dueOnly, startedOnly, startCardId]);

  // Test mode: reset per-snippet results when cards or isTestMode changes
  useEffect(() => {
    if (isTestMode && cards.length > 0) {
      setTestModeSnippetResults(Array(cards.length).fill(null));
    } else {
      setTestModeSnippetResults([]);
    }
  }, [isTestMode, cards.length]);

  // Save cookbook position whenever currentCardIndex changes
  useEffect(() => {
    // Skip if cards haven't loaded yet or index is invalid
    if (!deck || !cards.length || currentCardIndex < 0 || currentCardIndex >= cards.length) {
      return;
    }

    // Skip saving if we just loaded a saved position (give it time to settle)
    if (hasLoadedSavedPosition.current && currentCardIndex === 0) {
      // Reset the flag after a short delay
      setTimeout(() => {
        hasLoadedSavedPosition.current = false;
      }, 1000);
      return;
    }

    const currentCard = cards[currentCardIndex];
    if (!currentCard || !currentCard.id) {
      return;
    }

    // Add a small delay to avoid saving during initial load/navigation
    // This prevents saving the position when we're navigating TO a saved position
    const saveTimeout = setTimeout(() => {
      // Check if this is a cookbook by checking deck type
      const checkAndSavePosition = async () => {
        try {
          let deckId;
          if (typeof deck === 'string') {
            deckId = deck;
          } else if (deck && typeof deck === 'object') {
            deckId = deck.deckId || deck.id;
          } else {
            return;
          }

          const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
          const deckDoc = await getDoc(deckRef);

          if (deckDoc.exists()) {
            const deckData = deckDoc.data();
            if (deckData.type === 'cookbook') {
              // Save position for cookbooks
              await saveCookbookPosition(currentCard.id);
            }
          }
        } catch (error) {
          console.error('Error checking deck type for position save:', error);
        }
      };

      checkAndSavePosition();
    }, 500); // 500ms delay to avoid saving during initial navigation

    return () => clearTimeout(saveTimeout);
  }, [currentCardIndex, cards, deck, userId]);

  // Initialize leveling fields for a snippet if they don't exist
  const initializeSnippetLeveling = async (snippetId, snippetData) => {
    // Check if this is a temporary course card (from CourseViewer) that doesn't exist in Firestore
    // Deck cards with course metadata should still be saved
    const isTemporaryCourseCard = !deck && (snippetData.courseId || snippetData.courseTitle);

    if (snippetData.level !== undefined) {
      if (snippetData.currentLineIndex === undefined) {
        // Only try to update if it's a real snippet/deck card, not a temporary course card
        if (!isTemporaryCourseCard) {
          try {
            if (!deck) {
              return { ...snippetData, currentLineIndex: 0 };
            }
            const deckId = typeof deck === 'string' ? deck : (deck.deckId || deck.id);
            const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${snippetId}`);
            await updateDoc(snippetRef, { currentLineIndex: 0 });
            console.log(`Added currentLineIndex field to ${deck ? 'card' : 'snippet'} ${snippetId}`);
            return {
              ...snippetData,
              currentLineIndex: 0
            };
          } catch (error) {
            console.error(`Error adding currentLineIndex to ${deck ? 'card' : 'snippet'} ${snippetId}:`, error);
          }
        }
        // For course cards, just add currentLineIndex locally
        return {
          ...snippetData,
          currentLineIndex: 0
        };
      }
      return snippetData;
    }

    const levelingFields = {
      level: 1,
      current_exp: 0,
      exp_to_level: levelingSystem.calculateExpToLevel(2),
      currentLineIndex: 0
    };

    // Only try to update Firestore if it's a real snippet/deck card, not a temporary course card
    if (!isTemporaryCourseCard) {
      try {
        if (!deck) {
          return { ...snippetData, ...levelingFields };
        }
        const deckId = typeof deck === 'string' ? deck : (deck.deckId || deck.id);
        const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${snippetId}`);
        await updateDoc(snippetRef, levelingFields);
        console.log(`Initialized leveling for ${deck ? 'card' : 'snippet'} ${snippetId}`);
      } catch (error) {
        console.error(`Error initializing leveling for ${deck ? 'card' : 'snippet'} ${snippetId}:`, error);
      }
    } else {
      console.log(`Course card ${snippetId} - adding leveling fields locally only`);
    }

    return {
      ...snippetData,
      ...levelingFields
    };
  };

  // Save cookbook position (current card ID) to deck document
  const saveCookbookPosition = async (cardId) => {
    if (!deck || !userId || !cardId) return;

    try {
      let deckId;
      if (typeof deck === 'string') {
        deckId = deck;
      } else if (deck && typeof deck === 'object') {
        deckId = deck.deckId || deck.id;
      } else {
        return; // Invalid deck format
      }

      // Check if this is a cookbook (type === 'cookbook')
      const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
      const deckDoc = await getDoc(deckRef);

      if (!deckDoc.exists()) {
        console.warn('Deck not found when saving position');
        return;
      }

      const deckData = deckDoc.data();
      if (deckData.type !== 'cookbook') {
        // Only save position for cookbooks
        return;
      }

      const now = new Date();
      await updateDoc(deckRef, {
        lastCardId: cardId,
        lastAccessed: now,
        lastAccessedAt: now
      });
    } catch (error) {
      console.error('Error saving cookbook position:', error);
    }
  };

  // Load deck and cards
  const loadDeckAndCards = async (startCardId = null) => {
    try {
      console.log('Loading cards for deck:', deck);
      let deckId;
      if (typeof deck === 'string') {
        deckId = deck;
      } else if (deck && typeof deck === 'object') {
        deckId = deck.deckId || deck.id;
      } else {
        deckId = deck;
      }
      console.log('Loading cards from:', `users/${userId}/decks/${deckId}/cards`);
      console.log('Deck ID being used:', deckId);
      console.log('User ID:', userId);
      const cardsRef = collection(db, `users/${userId}/decks/${deckId}/cards`);
      const snapshot = await getDocs(cardsRef);

      console.log('Cards found in deck:', snapshot.docs.length);
      if (snapshot.empty) {
        console.error('No cards found in deck. Deck ID:', deckId, 'User ID:', userId);
        setError('No cards found in this deck');
        return;
      }

      const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
      const deckDoc = await getDoc(deckRef);
      const isCookbook = deckDoc.exists() && deckDoc.data().type === 'cookbook';

      const progressRows = await srs.getDeckCardProgressList(deckId);
      const dueCards = srs.filterDueCardProgressList(progressRows);
      let allCards = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const cardData = docSnap.data();
        const updatedCardData = await initializeSnippetLeveling(docSnap.id, cardData);
        const card = { id: docSnap.id, ...updatedCardData };
        return {
          ...card,
          progress: findProgressForCard(card, progressRows)
        };
      }));

      allCards = allCards.filter(card => card.solution);
      if (dueOnly) {
        const dueFiltered = allCards.filter((card) =>
          dueCards.some((dc) => cardMatchesProgressRow(card, dc))
        );
        if (dueFiltered.length > 0) {
          allCards = dueFiltered;
        } else if (startCardId) {
          // Stale due queue or scheduling mismatch: open the targeted card for practice
          const targeted = allCards.filter(
            (card) => card.id === startCardId || card.roadmapSnippetId === startCardId
          );
          if (targeted.length === 0) {
            setError('No cards due for review in this cookbook');
            return;
          }
          allCards = targeted;
        } else {
          setError('No cards due for review in this cookbook');
          return;
        }
      } else if (startedOnly) {
        const startedFiltered = allCards.filter((card) =>
          isSnippetStarted(statsFromCookbookCard(card, card.progress))
        );
        if (startedFiltered.length === 0) {
          setError('No started snippets in this cookbook');
          return;
        }
        allCards = startedFiltered;
      } else if (allCards.length === 0) {
        setError('No cards with solutions found in this deck');
        return;
      }

      allCards.sort((a, b) => {
        if (dueOnly || !isCookbook) {
          if (!a.progress && !b.progress) return 0;
          if (!a.progress) return 1;
          if (!b.progress) return -1;
          return new Date(a.progress.nextReview) - new Date(b.progress.nextReview);
        }

        const cardCreatedAtMs = (card) => {
          const t = card?.createdAt;
          if (!t) return 0;
          if (typeof t.toMillis === 'function') return t.toMillis();
          if (typeof t.seconds === 'number') return t.seconds * 1000;
          if (t instanceof Date) return t.getTime();
          return 0;
        };
        const byCreated = cardCreatedAtMs(a) - cardCreatedAtMs(b);
        if (byCreated !== 0) return byCreated;
        return String(a.id).localeCompare(String(b.id));
      });

      // Explicit startCardId only — cookbooks always open on the first snippet unless targeted.
      let targetCardId = startCardId || null;

      // Find the target card index BEFORE setting cards (to avoid timing issues).
      // For roadmap-derived decks, callers may pass the source snippet id rather than the card
      // document id (e.g. clicking a snippet row on /home/roadmap/graph), so fall back to a
      // roadmapSnippetId match before giving up.
      let targetCardIndex = 0;
      if (targetCardId) {
        let foundIndex = allCards.findIndex(card => card.id === targetCardId);
        if (foundIndex === -1) {
          foundIndex = allCards.findIndex(card => card.roadmapSnippetId === targetCardId);
        }
        if (foundIndex !== -1) {
          targetCardIndex = foundIndex;
        } else {
          console.warn(`Card ${targetCardId} not found in deck. Starting from first card.`);
        }
      }

      // Set cards and navigate to target in the same update cycle
      setCards(allCards);

      // Mark that we've loaded the saved position to prevent it from being overridden
      if (targetCardId && targetCardIndex > 0) {
        hasLoadedSavedPosition.current = true;
      }

      setCurrentCardIndex(targetCardIndex);

      setError('');
    } catch (error) {
      console.error('Error loading deck and cards:', error);
      setError('Failed to load deck content. Please try again.');
    }
  };

  // Navigate to a specific card by ID (in test mode, cannot jump to an already-answered card)
  const navigateToCard = (cardId) => {
    if (!cards.length) return;

    const cardIndex = cards.findIndex(card => card.id === cardId);
    if (cardIndex === -1) {
      console.warn(`Card ${cardId} not found in deck`);
      return;
    }
    if (isTestMode && testModeSnippetResults[cardIndex] != null) return;
    setCurrentCardIndex(cardIndex);
    console.log(`Navigated to card: ${cards[cardIndex].title} (index: ${cardIndex})`);
  };

  // Load stats for current card
  const loadStats = async () => {
    if (!cards.length) return;
    const currentCard = cards[currentCardIndex];
    try {
      const deckId = resolveStudyCardDeckId(deck, currentCard);
      const cardStats = deckId
        ? await srs.getCardStats(currentCard.id)
        : await srs.getSnippetStats(currentCard.id);
      setStats(cardStats);
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Failed to load statistics');
    }
  };

  // Load notes for current card
  const loadNotes = async () => {
    if (!cards.length) return;
    const currentCard = cards[currentCardIndex];
    try {
      const deckId = resolveStudyCardDeckId(deck, currentCard);
      if (!deckId) {
        setNotes('');
        return;
      }
      const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${currentCard.id}`);

      const snippetDoc = await getDoc(snippetRef);
      if (snippetDoc.exists()) {
        const snippetData = snippetDoc.data();
        setNotes(snippetData.notes || '');
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  // Save notes with debouncing
  const saveNotesDebounced = useCallback(
    (() => {
      let timeout;
      return (newNotes) => {
        if (timeout) clearTimeout(timeout);
        setIsSavingNotes(true);

        timeout = setTimeout(async () => {
          if (!cards.length) return;
          const currentCard = cards[currentCardIndex];
          const deckId = resolveStudyCardDeckId(deck, currentCard);
          if (!deckId) return;
          try {
            const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${currentCard.id}`);
            await updateDoc(snippetRef, { notes: newNotes });
            setIsSavingNotes(false);
          } catch (error) {
            console.error('Error saving notes:', error);
            setIsSavingNotes(false);
          }
        }, 1000);
      };
    })(),
    [cards, currentCardIndex, deck, userId]
  );

  // Handle notes change
  const handleNotesChange = (newNotes) => {
    setNotes(newNotes);
    saveNotesDebounced(newNotes);
  };

  // Update daily progress
  const updateDailyProgress = async () => {
    try {
      const today = new Date().toDateString();
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const dailyProgress = userData.dailyProgress || {};
        const todayProgress = dailyProgress[today] || 0;
        const isFirstRepOfDay = todayProgress === 0;

        // Build the analytics patch alongside the existing daily-progress write
        // so we never need a second round-trip.
        const updatePayload = {
          [`dailyProgress.${today}`]: todayProgress + 1
        };

        // --- first_rep_completed (one-shot, lifetime per user) ---
        const isFirstEverRep = !userData.firstRepCompletedAt;
        if (isFirstEverRep) {
          updatePayload.firstRepCompletedAt = serverTimestamp();
        }

        // --- streak_milestone (3 / 7 / 30 day streaks) ---
        // Recompute streak from the *post-write* daily map so today counts.
        // Only meaningful when this is the first rep of the day; subsequent
        // reps the same day can't change the streak value.
        let newMilestone = null;
        if (isFirstRepOfDay) {
          const projectedDailyProgress = {
            ...dailyProgress,
            [today]: 1
          };
          const dayStreak = computeDayStreak(projectedDailyProgress, new Date());
          const STREAK_MILESTONES = [3, 7, 30];
          const reached = Array.isArray(userData.streakMilestonesReached)
            ? userData.streakMilestonesReached
            : [];
          if (STREAK_MILESTONES.includes(dayStreak) && !reached.includes(dayStreak)) {
            newMilestone = dayStreak;
            updatePayload.streakMilestonesReached = arrayUnion(dayStreak);
          }
        }

        await updateDoc(userRef, updatePayload);

        // Fire events only after the Firestore write succeeds so a failed
        // write doesn't leave us with double-fired milestones on retry.
        if (isFirstEverRep) {
          const currentCard = cards[currentCardIndex];
          const deckId = typeof deck === 'string' ? deck : deck?.id || deck?.deckId;
          // attempt_count = total submits on this card before the success
          // landed (1 = got it on the first try). High values on the demo's
          // hello-world card are a friction signal on the demo itself —
          // typing tolerance too strict, instructions unclear, etc.
          const attemptCount = Math.max(1, cardAttemptCountRef.current);
          trackFirstRepCompleted({
            deck_id: deckId,
            card_id: currentCard?.id,
            language: currentCard?.language,
            attempt_count: attemptCount
          });
        }
        if (newMilestone !== null) {
          trackStreakMilestone(newMilestone);
        }

        console.log(`Updated daily progress: ${todayProgress + 1} snippets completed today`);
      }
    } catch (error) {
      console.error('Error updating daily progress:', error);
    }
  };

  const updateDailyMasteredProgress = async () => {
    try {
      const today = new Date().toDateString();
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) return;

      const userData = userSnap.data();
      const dailyMasteredProgress = userData.dailyMasteredProgress || {};
      const todayMastered = dailyMasteredProgress[today] || 0;
      await updateDoc(userRef, {
        [`dailyMasteredProgress.${today}`]: todayMastered + 1
      });
    } catch (error) {
      console.error('Error updating daily mastered progress:', error);
    }
  };

  /** Course level is updated from CourseViewer / course doc; legacy users/{uid}/snippets rows removed. */
  const updateCourseLevel = async (courseId) => {
    if (!courseId) return;
  };

  // Reset state for new card/line
  const resetState = () => {
    setShowAnswer(false);
    setUserCode('');
    setHasStartedCoding(false);
    setError('');
    setFeedback('');
    setIsCorrect(false);
    setExplanation('');
    setFlashcardViewMode('study');
    setLineComparison(null); // Clear comparison when resetting
    // Per-card attempt counter resets when the card changes; aggregates
    // across both regular-mode submits and line-by-line submits on the
    // *same* card.
    cardAttemptCountRef.current = 0;
    srsReviewCommittedRef.current = false;
    srsReviewResultRef.current = null;
  };

  /**
   * Clear the editor after Again → Try Again without resetting SRS visit
   * tracking (attempt count + whether this card was already graded).
   */
  const resetEditorForRetry = () => {
    setShowAnswer(false);
    setUserCode('');
    setHasStartedCoding(false);
    setError('');
    setFeedback('');
    setIsCorrect(false);
    setLineComparison(null);
  };

  const resetStateForNewLine = () => {
    console.log('resetStateForNewLine called:', {
      studyMode,
      linesLength: lines.length,
      currentLineIndex,
      currentLine: lines[currentLineIndex]
    });

    // In line-by-line mode, show the current line initially
    if (studyMode === 'lineByLine' && lines.length > 0 && currentLineIndex < lines.length) {
      console.log('Setting userCode to current line:', lines[currentLineIndex]);
      setUserCode(lines[currentLineIndex]);
    } else {
      console.log('Setting userCode to empty string');
      setUserCode('');
    }
    setHasStartedCoding(false);
    setShowLine(false);
    setFeedback('');
    setIsCorrect(false);
    setLineComparison(null); // Clear comparison when resetting
    setFlashcardViewMode('study');
  };

  // Navigation methods
  const handleNext = () => {
    if (currentCardIndex < cards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      resetState();
    } else if (deck) {
      loadDeckAndCards();
      setCurrentCardIndex(0);
    }
  };

  const handlePrevious = () => {
    if (isTestMode) return;
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      resetState();
    }
  };

  // Update card progress in parent component
  const updateCardProgress = (cardId, lineIndex) => {
    console.log(`StudyLogic: Updating card ${cardId} progress to line ${lineIndex}`);
    const updatedCards = cards.map(card => {
      if (card.id === cardId) {
        return { ...card, currentLineIndex: lineIndex };
      }
      return card;
    });
    setCards(updatedCards);
  };

  // Code validation methods
  const analyzeCode = useCallback((code) => {
    let result = {
      output: [],
      error: null,
      structure: {
        hasLoops: false,
        hasConditionals: false,
        hasFunctions: false,
        variables: new Set(),
        functionCalls: new Set()
      }
    };

    try {
      const functionPattern = /function\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g;
      const variablePattern = /(?:let|const|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=/g;
      const functionCallPattern = /([a-zA-Z_$][a-zA-Z0-9_$]*)\s*\(/g;
      const consoleLogPattern = /console\.log\s*\((.*?)\)/g;
      const loopPattern = /(?:for|while|do)\s*\(/g;
      const conditionalPattern = /(?:if|switch)\s*\(/g;

      result.structure.hasFunctions = functionPattern.test(code);
      result.structure.hasLoops = loopPattern.test(code);
      result.structure.hasConditionals = conditionalPattern.test(code);

      let match;
      while ((match = variablePattern.exec(code)) !== null) {
        result.structure.variables.add(match[1]);
      }

      functionCallPattern.lastIndex = 0;
      while ((match = functionCallPattern.exec(code)) !== null) {
        if (!/function\s+[a-zA-Z_$][a-zA-Z0-9_$]*\s*\($/.test(code.slice(Math.max(0, match.index - 9), match.index))) {
          result.structure.functionCalls.add(match[1]);
        }
      }

      consoleLogPattern.lastIndex = 0;
      while ((match = consoleLogPattern.exec(code)) !== null) {
        try {
          const logContent = match[1].trim();
          if (/^['"].*['"]$/.test(logContent)) {
            result.output.push([logContent.slice(1, -1)]);
          } else if (/^\d+$/.test(logContent)) {
            result.output.push([parseInt(logContent, 10)]);
          } else {
            result.output.push(["[Expression output]"]);
          }
        } catch (e) {
          result.output.push(["[Expression output]"]);
        }
      }
    } catch (e) {
      result.error = e;
    }

    return result;
  }, []);

  const checkCodeSolution = useCallback(async () => {
    if (!userCode.trim()) {
      console.log('checkCodeSolution: No user code to check');
      return;
    }

    // Only real submissions count toward attempt_count — empty checks (the
    // guard above) are bounced before we get here.
    cardAttemptCountRef.current += 1;

    console.log('checkCodeSolution: Starting validation...', {
      userCode: userCode.trim(),
      currentCardIndex,
      hasCards: cards.length > 0
    });

    const currentCard = cards[currentCardIndex];
    const language = currentCard.language || deck?.language || 'JavaScript';

    try {
      let userCodeLines, solutionLines;
      try {
        userCodeLines = await splitCodeIntoLinesAsync(userCode, language, userId);
      } catch (userCodeError) {
        console.error('Error processing user code, using fallback:', userCodeError);
        userCodeLines = simpleCodeProcessing(userCode, language);
      }

      try {
        solutionLines = await splitCodeIntoLinesAsync(currentCard.solution, language, userId);
      } catch (solutionError) {
        console.error('Error processing solution, using fallback:', solutionError);
        solutionLines = simpleCodeProcessing(currentCard.solution, language);
      }

      const filteredUserCode = userCodeLines.join('\n');
      const filteredSolution = solutionLines.join('\n');

      let normalizedUserCode, normalizedSolution;
      try {
        normalizedUserCode = normalizeCode(filteredUserCode, language);
        normalizedSolution = normalizeCode(filteredSolution, language);
      } catch (normalizeError) {
        console.error('Error normalizing code, using fallback:', normalizeError);
        normalizedUserCode = simpleNormalizeCode(filteredUserCode, language);
        normalizedSolution = simpleNormalizeCode(filteredSolution, language);
      }

      const wasExactMatch = normalizedUserCode === normalizedSolution;
      const normalizedUserLines = userCodeLines.map((line) => {
        try {
          return normalizeCode(line, language);
        } catch {
          return simpleNormalizeCode(line, language);
        }
      });
      const normalizedSolutionLines = solutionLines.map((line) => {
        try {
          return normalizeCode(line, language);
        } catch {
          return simpleNormalizeCode(line, language);
        }
      });
      const lineMetrics = computeLineAccuracy(normalizedUserLines, normalizedSolutionLines);
      const priorFailedChecks = cardAttemptCountRef.current > 1;
      const reviewMetrics = buildCheckReviewMetrics({
        lineMetrics,
        priorFailedChecks,
        wasExactMatch
      });

      if (studyMode === 'regular' && !isTestMode) {
        trackSrsGradeInferred({
          rating: reviewMetrics.rating,
          accuracy: reviewMetrics.accuracy,
          total_lines: reviewMetrics.totalLines,
          wrong_line_count: lineMetrics.wrongLineCount,
          prior_failed_checks: priorFailedChecks,
          study_mode: studyMode,
          language,
          deck_id: typeof deck === 'string' ? deck : (deck?.deckId || deck?.id),
          card_id: currentCard?.id
        });
      }

      console.log('checkCodeSolution: Comparison results', {
        filteredUserCode,
        filteredSolution,
        normalizedUserCode,
        normalizedSolution,
        wasExactMatch,
        reviewMetrics
      });

      /**
       * Resolve roadmap track/node ids up-front so BOTH termination paths (wrong-answer
       * early return and last-snippet success) can record the attempt. Prefer explicit
       * roadmap props (set when test started), then deck, then current card.
       */
      const currentCardForDeck = cards[currentCardIndex];
      const effectiveTrackId =
        roadmapTrackIdProp ??
        resolveTrackIdFromDeckAndCard(deck, currentCardForDeck);
      const effectiveNodeId =
        roadmapNodeIdProp ??
        resolveNodeIdFromDeckAndCard(deck, currentCardForDeck);

      if (isTestMode && cards.length > 0 && (!effectiveTrackId || !effectiveNodeId)) {
        console.warn('StudyLogic: test mode but missing track/node ids', {
          roadmapTrackIdProp,
          roadmapNodeIdProp,
          deckTrackId: deck?.trackId,
          deckNodeId: deck?.nodeId,
          cardTrackId: currentCardForDeck?.roadmapTrackId,
          cardNodeId: currentCardForDeck?.roadmapNodeId
        });
      }

      // Test mode: wrong answer → test ended, show completion summary (no retries)
      if (isTestMode && !wasExactMatch) {
        if (currentCard?.id) {
          try {
            const deckId = resolveStudyCardDeckId(deck, currentCard);
            if (deckId) {
              await srs.updateCardProgress(deckId, currentCard.id, false);
              notifyReviewQueueChanged();
            }
          } catch (srsErr) {
            console.error('StudyLogic: SRS update failed (test mode incorrect)', srsErr);
          }
        }
        const correctSoFar = (testModeSnippetResults || []).filter(r => r === true).length;
        const totalCount = cards.length;
        // Final score for a failed test: correct snippets so far over total. Remaining
        // unanswered snippets count as wrong since the test ends immediately on a miss.
        if (userId && effectiveTrackId && effectiveNodeId && totalCount > 0) {
          const failPercentage = Math.round((correctSoFar / totalCount) * 100);
          try {
            await roadmapService.recordNodeTestAttempt(userId, effectiveTrackId, effectiveNodeId, {
              percentage: failPercentage,
              passed: false
            });
          } catch (attemptErr) {
            console.warn('StudyLogic: recordNodeTestAttempt (fail path) failed', attemptErr);
          }
        }
        setFeedback('❌ Not quite right.');
        setShowAnswer(true);
        setTestCompleteSummary({ correctCount: correctSoFar, totalCount, passed: false });
        return;
      }

      setIsCorrect(wasExactMatch);
      const srsSchedulingPaused = studyMode === 'regular'
        && !isTestMode
        && isSrsPracticeOnlyForStudy(currentCard?.progress, new Date(), { isTestMode });
      setFeedback(formatCheckRecallFeedback({
        wasExactMatch,
        reviewMetrics,
        srsSchedulingPaused
      }));
      setShowAnswer(true);
      handleAnswer(wasExactMatch, reviewMetrics);

      if (isTestMode && effectiveTrackId && effectiveNodeId && cards.length > 0) {
        const nextResults = testModeSnippetResults.length === cards.length
          ? [...testModeSnippetResults]
          : Array(cards.length).fill(null);
        nextResults[currentCardIndex] = wasExactMatch;
        setTestModeSnippetResults(nextResults);

        const isLastSnippet = currentCardIndex === cards.length - 1;
        if (isLastSnippet) {
          const correctCount = nextResults.filter(r => r === true).length;
          const totalCount = nextResults.length;
          const score = correctCount / totalCount;
          const percentage = Math.round(score * 100);
          const passed = score >= TEST_MODE_PASSING_SCORE;
          if (userId) {
            try {
              await roadmapService.recordNodeTestAttempt(userId, effectiveTrackId, effectiveNodeId, { percentage, passed });
            } catch (attemptErr) {
              console.warn('StudyLogic: recordNodeTestAttempt failed', attemptErr);
            }
          }
          if (passed) {
            try {
              console.log('StudyLogic: marking node completed', { userId, trackId: effectiveTrackId, nodeId: effectiveNodeId, percentage });
              await roadmapService.markNodeCompleted(userId, effectiveTrackId, effectiveNodeId, percentage);
              onNodeCompleted?.({ trackId: effectiveTrackId, nodeId: effectiveNodeId, percentage });
            } catch (err) {
              console.error('StudyLogic: markNodeCompleted failed', err);
            }
          }
          setTestCompleteSummary({ correctCount, totalCount, passed });
        }
      }
    } catch (error) {
      console.error('Error checking solution:', error);
      setError('Failed to check solution');
    }
  }, [userCode, cards, currentCardIndex, deck, userId, studyMode, isTestMode, testModeSnippetResults, onNodeCompleted, roadmapTrackIdProp, roadmapNodeIdProp, setView, previousView]);

  // Compare two strings character by character and return segments
  const compareStrings = useCallback((userStr, solutionStr) => {
    const segments = [];
    // Ensure we're working with strings, not null/undefined
    const userStrSafe = userStr || '';
    const solutionStrSafe = solutionStr || '';
    const userLen = userStrSafe.length;
    const solutionLen = solutionStrSafe.length;
    const maxLen = Math.max(userLen, solutionLen);

    let i = 0;
    let currentSegment = { text: '', isCorrect: null, isMissing: false, startIndex: 0 };

    while (i < maxLen) {
      const userChar = i < userLen ? userStrSafe[i] : null;
      const solutionChar = i < solutionLen ? solutionStrSafe[i] : null;

      // Determine if this position is correct, missing, or wrong
      let isCorrect;
      let isMissing = false;
      let charToShow;

      if (userChar === null && solutionChar !== null) {
        // User is missing this character - this is the key case for missing text
        isCorrect = false;
        isMissing = true;
        charToShow = solutionChar;
      } else if (userChar !== null && solutionChar === null) {
        // User has extra character
        isCorrect = false;
        isMissing = false;
        charToShow = userChar;
      } else if (userChar === solutionChar) {
        // Characters match
        isCorrect = true;
        isMissing = false;
        charToShow = userChar;
      } else {
        // Characters don't match (different characters at same position)
        isCorrect = false;
        isMissing = false;
        charToShow = userChar || solutionChar;
      }

      // Start a new segment if correctness or missing status changes
      const segmentChanged = currentSegment.isCorrect !== null &&
        (currentSegment.isCorrect !== isCorrect || currentSegment.isMissing !== isMissing) &&
        currentSegment.text.length > 0;

      if (segmentChanged) {
        segments.push(currentSegment);
        currentSegment = { text: '', isCorrect: isCorrect, isMissing: isMissing, startIndex: i };
      }

      // Initialize segment if empty
      if (currentSegment.text.length === 0) {
        currentSegment.isCorrect = isCorrect;
        currentSegment.isMissing = isMissing;
        currentSegment.startIndex = i;
      }

      // Add character to current segment (always add, even if it's a space or special char)
      if (charToShow !== null && charToShow !== undefined) {
        currentSegment.text += charToShow;
      }

      i++;
    }

    // Add the last segment if it has content
    if (currentSegment.text.length > 0) {
      segments.push(currentSegment);
    }

    return segments;
  }, []);

  const checkLine = useCallback(() => {
    if (!userCode.trim()) {
      setFeedback('Please enter some code before checking.');
      return;
    }

    // Count each genuine line submission as an attempt on the current card.
    // Aggregated with regular-mode submits in cardAttemptCountRef so the
    // first_rep_completed event sees total fumbling on the card.
    cardAttemptCountRef.current += 1;

    // Validate that we have lines and a valid currentLineIndex
    if (!lines || lines.length === 0) {
      console.error('checkLine: No lines available');
      setError('No lines available. Please refresh the page.');
      return;
    }

    if (currentLineIndex < 0 || currentLineIndex >= lines.length) {
      console.error('checkLine: Invalid currentLineIndex', { currentLineIndex, linesLength: lines.length });
      setError('Invalid line index. Please refresh the page.');
      return;
    }

    if (!cards || cards.length === 0 || currentCardIndex < 0 || currentCardIndex >= cards.length) {
      console.error('checkLine: Invalid card index', { currentCardIndex, cardsLength: cards.length });
      setError('Invalid card. Please refresh the page.');
      return;
    }

    const currentCard = cards[currentCardIndex];
    const language = currentCard.language || deck?.language || 'JavaScript';
    const currentLine = lines[currentLineIndex];

    // Validate currentLine exists
    if (!currentLine) {
      console.error('checkLine: Current line is undefined', { currentLineIndex, linesLength: lines.length });
      setError('Current line is not available. Please refresh the page.');
      return;
    }

    console.log('checkLine: Starting validation', {
      userCode: userCode.trim(),
      currentLine,
      currentLineIndex,
      linesLength: lines.length,
      language,
      hasLines: lines.length > 0,
      allLines: lines // Show all lines for debugging
    });

    const isExactMatch = userCode === currentLine;
    const normalizedUserCode = normalizeCode(userCode, language);
    const normalizedSolution = normalizeCode(currentLine, language);
    const isCorrect = normalizedUserCode === normalizedSolution;

    console.log('Check line comparison:', {
      userCode,
      currentLine,
      isExactMatch,
      normalizedUserCode,
      normalizedSolution,
      isCorrect
    });

    const forceCorrect = isExactMatch || normalizedUserCode === normalizedSolution;
    setIsCorrect(forceCorrect);
    setLineResults(prevResults => {
      const newResults = [...prevResults];
      newResults[currentLineIndex] = forceCorrect;
      return newResults;
    });

    // Compute character-by-character comparison for visual feedback
    if (forceCorrect) {
      // For correct answers, show all green
      setLineComparison({
        segments: [{ text: userCode || currentLine, isCorrect: true, isMissing: false, startIndex: 0 }],
        userText: userCode,
        solutionText: currentLine
      });
    } else {
      // For incorrect answers, show character-by-character comparison
      // Use raw strings (not normalized) for accurate character-by-character display
      const comparison = compareStrings(userCode, currentLine);
      console.log('Character-by-character comparison:', {
        userCode,
        currentLine,
        userLength: userCode.length,
        solutionLength: currentLine.length,
        segments: comparison,
        missingSegments: comparison.filter(s => s.isMissing)
      });
      setLineComparison({
        segments: comparison,
        userText: userCode,
        solutionText: currentLine
      });
    }

    if (forceCorrect) {
      setFeedback(' Correct! Next line...');
      if (currentLineIndex === lines.length - 1) {
        handleCompletion();
      } else {
        const nextLineIndex = currentLineIndex + 1;
        setTimeout(() => {
          setCurrentLineIndex(nextLineIndex);
          saveLineProgress(currentCard.id, nextLineIndex);
          resetStateForNewLine();
          setLineComparison(null); // Clear comparison when moving to next line
        }, 1000);
      }
    } else {
      setInitiallyWrongLines(prevWrongLines => {
        if (!prevWrongLines.includes(currentLineIndex)) {
          const newWrongLines = [...prevWrongLines, currentLineIndex];

          // Check if this is a temporary course card (from CourseViewer) that doesn't exist in Firestore
          // Deck cards with course metadata should still be saved
          const isTemporaryCourseCard = !deck && (currentCard.courseId || currentCard.courseTitle);

          if (!isTemporaryCourseCard) {
            const deckId = resolveStudyCardDeckId(deck, currentCard);
            if (deckId) {
              try {
                const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${currentCard.id}`);
                updateDoc(snippetRef, { initiallyWrongLines: newWrongLines })
                  .then(() => {
                    console.log(`Saved wrong lines for ${deck ? 'card' : 'snippet'} ${currentCard.id}:`, newWrongLines);
                  })
                  .catch(error => {
                    console.error(`Error saving wrong lines for ${deck ? 'card' : 'snippet'} ${currentCard.id}:`, error);
                  });
              } catch (error) {
                console.error(`Error preparing to save wrong lines for next session:`, error);
              }
            }
          } else {
            console.log(`Course card ${currentCard.id} - skipping wrong lines save to Firestore`);
          }

          return newWrongLines;
        }
        return prevWrongLines;
      });

      if (currentLineIndex === lines.length - 1) {
        setFeedback('❌ Not quite right. This is the last line. You can try again or view the solution.');
        setShowLine(true);
      } else {
        setFeedback('❌ Not quite right, try again!');
        setShowLine(true);
      }

      // Don't clear the input - let user see the comparison
      // setUserCode('');
      // setHasStartedCoding(false);
    }
  }, [userCode, currentCardIndex, cards, deck, lines, currentLineIndex, compareStrings]);

  // Format SRS next review for display (minutes when learning, days when review).
  const formatNextReview = (progressOrDate) => {
    if (!progressOrDate) return '';
    const progress =
      typeof progressOrDate === 'object' && progressOrDate.nextReview
        ? progressOrDate
        : { nextReview: progressOrDate };
    const label = formatSrsScheduleLabel(progress);
    if (!label) return '';
    return ` Next review: ${label}.`;
  };

  const buildSrsExperienceFields = (progressResult) => {
    if (!progressResult) return {};
    const scheduleLabel = formatSrsScheduleLabel(progressResult);
    const fields = {
      stillInLearning: isCardInLearningPhase(progressResult),
      committedSrsProgress: progressResult
    };
    if (progressResult.lastRating) {
      fields.srsRating = progressResult.lastRating;
      fields.srsInterval = Number.isFinite(Number(progressResult.interval))
        ? Number(progressResult.interval)
        : undefined;
      fields.srsIntervalMinutes = Number.isFinite(Number(progressResult.intervalMinutes))
        ? Number(progressResult.intervalMinutes)
        : undefined;
      fields.srsScheduleLabel = scheduleLabel;
    }
    if (progressResult.newlyMastered) {
      fields.newlyMastered = true;
    }
    if (progressResult.is_mastered) {
      fields.isMastered = true;
    }
    return fields;
  };

  // Experience and leveling methods
  const maybeAwardRoleExpForCookbookStudy = async (currentCard) => {
    if (!userId || isTestMode) return null;

    const deckData = typeof deck === 'object' && deck ? deck : null;
    if (!isRoleEligibleCookbookDeck(deckData)) return null;

    const trackId =
      roadmapTrackIdProp ?? resolveTrackIdFromDeckAndCard(deckData, currentCard);
    if (!trackId) return null;

    const nodeId =
      roadmapNodeIdProp ?? resolveNodeIdFromDeckAndCard(deckData, currentCard);

    try {
      const result = await awardRoleExpForStudy(userId, trackId, undefined, nodeId);
      if (result?.updates?.length) {
        for (const update of result.updates) {
          if (update.leveledUp) {
            trackRoleLevelUp({
              role_id: update.roleId,
              level: update.level,
              track_id: trackId
            });
          }
        }
      }
      return result;
    } catch (err) {
      console.error('StudyLogic: awardRoleExpForStudy failed', err);
      return null;
    }
  };

  const commitDeckSrsReview = async (srsPayload, { sameSessionContinue = false } = {}) => {
    const currentCard = cards[currentCardIndex];
    const deckId = resolveStudyCardDeckId(deck, currentCard);
    const progressSource = srsReviewResultRef.current || currentCard?.progress;

    if (
      deckId &&
      currentCard?.id &&
      shouldDeferFirstExposureSrs(progressSource, { isTestMode })
    ) {
      try {
        const orientationResult = await srs.recordFirstExposureOrientation(
          deckId,
          currentCard.id
        );
        srsReviewResultRef.current = orientationResult;
        setCards((prev) =>
          prev.map((card) =>
            card.id === currentCard.id
              ? mergeCardSrsProgress(card, orientationResult)
              : card
          )
        );
        return orientationResult;
      } catch (error) {
        console.error('StudyLogic: first-exposure orientation failed', error);
        return null;
      }
    }

    if (
      !shouldCommitDeckSrsPayload({
        studyMode,
        isTestMode,
        hasDeck: Boolean(deckId),
        hasCard: Boolean(currentCard?.id),
        srsReviewCommitted: srsReviewCommittedRef.current,
        progressSource,
        rating: srsPayload.rating
      })
    ) {
      return srsReviewCommittedRef.current ? srsReviewResultRef.current : null;
    }

    const commitOptions = buildDeckSrsCommitOptions({
      progressSource,
      rating: srsPayload.rating
    });

    try {
      const progressResult = await srs.updateCardProgress(deckId, currentCard.id, {
        ...srsPayload,
        sameSessionContinue: sameSessionContinue || commitOptions.sameSessionContinue
      });
      if (commitOptions.markReviewCommitted) {
        srsReviewCommittedRef.current = true;
      }
      srsReviewResultRef.current = progressResult;
      notifyReviewQueueChanged();
      setCards((prev) =>
        prev.map((card) =>
          card.id === currentCard.id
            ? mergeCardSrsProgress(card, progressResult)
            : card
        )
      );
      return progressResult;
    } catch (error) {
      console.error('StudyLogic: SRS review commit failed', error);
      return null;
    }
  };

  /**
   * Anki-style Again: explicit lapse without submitting code.
   */
  const handleSrsAgain = async () => {
    return commitDeckSrsReview(AGAIN_SRS_PAYLOAD);
  };

  const handleAnswer = async (wasExactMatch, reviewMetrics = null) => {
    const currentCard = cards[currentCardIndex];
    const srsPayload = reviewMetrics || {
      rating: wasExactMatch ? 'good' : 'again',
      accuracy: wasExactMatch ? 1 : 0,
      initiallyWrongCount: wasExactMatch ? 0 : 1,
      totalLines: 1
    };
    try {
      console.log('handleAnswer called with:', {
        wasExactMatch,
        srsPayload,
        currentCard: currentCard ? {
          id: currentCard.id,
          title: currentCard.title,
          level: currentCard.level,
          current_exp: currentCard.current_exp,
          exp_to_level: currentCard.exp_to_level
        } : null,
        deck: deck ? (typeof deck === 'string' ? deck : (deck.deckId || deck.id)) : null,
        userId
      });

      // Roadmap test mode: node completion (markNodeCompleted, onNodeCompleted) is handled in checkCodeSolution.
      // Skip leveling / EXP popup paths below, but still persist SRS so Track snippets and due dates match library behavior.
      if (isTestMode) {
        setIsCorrect(wasExactMatch);
        if (currentCard?.id) {
          try {
            const deckId = resolveStudyCardDeckId(deck, currentCard);
            if (deckId) {
              const progressResult = await srs.updateCardProgress(
                deckId,
                currentCard.id,
                srsPayload
              );
              notifyReviewQueueChanged();
              if (progressResult?.newlyMastered) {
                await updateDailyMasteredProgress();
              }
              setCards((prev) =>
                prev.map((card) =>
                  card.id === currentCard.id
                    ? mergeCardSrsProgress(card, progressResult)
                    : card
                )
              );
            }
          } catch (srsErr) {
            console.error('StudyLogic: SRS update failed in roadmap test mode', srsErr);
          }
        }
        if (wasExactMatch) {
          const isLastSnippet = currentCardIndex >= (cards?.length ?? 1) - 1;
          if (!isLastSnippet) {
            setTimeout(() => {
              handleNext();
              resetState();
            }, 2000);
          }
        }
        return;
      }

      if (!deck) {
        // Snippet study: update SRS only in test mode (learn mode does not update scheduling/stats)
        const isTestMode = studyMode === 'regular';
        const isTemporaryCourseCard = currentCard.courseId || currentCard.courseTitle;

        let snippetProgressResult = null;
        if (isTestMode && wasExactMatch) {
          try {
            const snippetId = currentCard.snippetId || currentCard.id;
            console.log(`Updating SRS progress for snippet ${snippetId} (test mode)`);
            snippetProgressResult = await srs.updateSnippetProgress(snippetId, true);
            notifyReviewQueueChanged();
            console.log('SRS progress update result:', snippetProgressResult);
          } catch (srsError) {
            console.error('Error updating SRS progress:', srsError);
          }
        }
        if (isTemporaryCourseCard && !isTestMode) {
          console.log(`Temporary course card ${currentCard.id} - no SRS update in learn mode`);
        }

        if (wasExactMatch) {
          await updateDailyProgress();
          try {
            const snippetId = currentCard.id;
            console.log(`Adding experience to ${isTemporaryCourseCard ? 'temporary course card' : 'snippet'} ${snippetId}`);

            // For temporary course cards, we can't update Firestore, so just simulate the experience
            let expResult;
            if (isTemporaryCourseCard) {
              // Simulate experience for temporary course cards (same cap as persisted cards)
              const sim = applySnippetExpGain(
                levelingSystem,
                currentCard.level || 1,
                currentCard.current_exp || 0,
                currentCard.exp_to_level,
                10
              );
              expResult = {
                leveledUp: sim.leveledUp,
                level: sim.level,
                current_exp: sim.current_exp,
                exp_to_level: sim.exp_to_level
              };
              console.log(`Simulated experience for temporary course card:`, expResult);
            } else {
              expResult = await levelingSystem.addSnippetExp(snippetId, 10);
            }

            const popupData = {
              snippetExp: 10,
              snippetLeveledUp: expResult?.leveledUp || false,
              level: expResult?.level,
              currentExp: expResult?.current_exp,
              expToLevel: expResult?.exp_to_level,
              nextReviewText: snippetProgressResult?.nextReview ? formatNextReview(snippetProgressResult.nextReview) : ''
            };

            console.log('Setting experience popup data:', popupData);
            setExperienceData(popupData);
            setShowExperiencePopup(true);

            setFeedback('');

            if (expResult) {
              setCards(prevCards => {
                return prevCards.map(card => {
                  if (card.id === snippetId) {
                    return {
                      ...card,
                      level: expResult.level,
                      current_exp: expResult.current_exp,
                      exp_to_level: expResult.exp_to_level
                    };
                  }
                  return card;
                });
              });
            }

            if (currentCard.courseId) {
              setTimeout(() => {
                updateCourseLevel(currentCard.courseId);
              }, 1000);
            }
          } catch (expError) {
            console.error('Error adding experience points:', expError);
            setFeedback('Great job! Keep up the streak!');
          }
        } else {
          setFeedback(
            formatCheckRecallFeedback({
              wasExactMatch,
              reviewMetrics: srsPayload,
              scheduleProgress: snippetProgressResult
            })
          );
        }
      } else {
        let cardProgressResult = null;
        let committedSrsThisCheck = false;
        let recordedOrientationThisCheck = false;
        const progressSource = srsReviewResultRef.current || currentCard?.progress;
        const deferOrientation = shouldDeferFirstExposureSrs(progressSource, { isTestMode });
        const srsSchedulingPaused = isSrsPracticeOnlyForStudy(
          progressSource,
          new Date(),
          { isTestMode }
        );
        const shouldCommitSrsReview = shouldCommitSrsOnCheck({
          studyMode,
          isTestMode,
          srsReviewCommitted: srsReviewCommittedRef.current,
          progressSource,
          rating: srsPayload.rating
        });

        if (deferOrientation) {
          cardProgressResult = await commitDeckSrsReview(srsPayload);
          recordedOrientationThisCheck = Boolean(cardProgressResult?.orientationCompleted);
        } else if (shouldCommitSrsReview) {
          const commitOptions = buildDeckSrsCommitOptions({
            progressSource,
            rating: srsPayload.rating
          });
          cardProgressResult = await commitDeckSrsReview(srsPayload, {
            sameSessionContinue: commitOptions.sameSessionContinue
          });
          committedSrsThisCheck = Boolean(cardProgressResult);
          if (cardProgressResult?.newlyMastered) {
            await updateDailyMasteredProgress();
          }
        }

        const srsResultForUi = srsReviewResultRef.current;

        if (wasExactMatch && currentCard.originalSnippetId) {
          await updateDailyProgress();
          try {
            console.log(`Adding experience to original snippet ${currentCard.originalSnippetId}`);
            const expResult = await levelingSystem.addSnippetExp(currentCard.originalSnippetId, 10);
            const roleExpResult = await maybeAwardRoleExpForCookbookStudy(currentCard);

            const popupData = {
              snippetExp: 10,
              snippetLeveledUp: expResult?.leveledUp || false,
              level: expResult?.level,
              currentExp: expResult?.current_exp,
              expToLevel: expResult?.exp_to_level,
              ...buildSrsExperienceFields(committedSrsThisCheck ? srsResultForUi : null),
              roleExpUpdates: roleExpResult?.updates ?? []
            };

            setExperienceData(popupData);
            setShowExperiencePopup(true);
            setFeedback('');

            if (expResult) {
              setCards(prevCards => {
                return prevCards.map(card => {
                  if (card.id === currentCard.id) {
                    return {
                      ...card,
                      level: expResult.level,
                      current_exp: expResult.current_exp,
                      exp_to_level: expResult.exp_to_level
                    };
                  }
                  return card;
                });
              });
            }

            if (currentCard.courseId) {
              setTimeout(() => {
                updateCourseLevel(currentCard.courseId);
              }, 1000);
            }
          } catch (expError) {
            console.error('Error adding experience points to original snippet:', expError);
            setFeedback('Great job! Keep up the streak!');
          }
        } else if (wasExactMatch && currentCard.level !== undefined) {
          await updateDailyProgress();
          try {
            console.log(`Updating card's own leveling: ${currentCard.id}`);
            const deckId = resolveStudyCardDeckId(deck, currentCard);
            if (!deckId) {
              throw new Error('Missing deck id for card leveling update');
            }
            const cardRef = doc(db, `users/${userId}/decks/${deckId}/cards/${currentCard.id}`);

            const gain = applySnippetExpGain(
              levelingSystem,
              currentCard.level || 1,
              currentCard.current_exp || 0,
              currentCard.exp_to_level,
              10
            );
            const { level, current_exp: currentExp, exp_to_level: expToLevel, leveledUp } = gain;

            await updateDoc(cardRef, {
              level,
              current_exp: currentExp,
              exp_to_level: expToLevel
            });

            const roleExpResult = await maybeAwardRoleExpForCookbookStudy(currentCard);

            setFeedback('');

            // Prepare experience popup data
            const popupData = {
              snippetExp: 10,
              snippetLeveledUp: leveledUp,
              level,
              currentExp,
              expToLevel,
              ...buildSrsExperienceFields(committedSrsThisCheck ? srsResultForUi : null),
              roleExpUpdates: roleExpResult?.updates ?? []
            };

            console.log('Setting experience popup data for deck card:', popupData);
            setExperienceData(popupData);
            setShowExperiencePopup(true);

            setCards(prevCards => {
              return prevCards.map(card => {
                if (card.id === currentCard.id) {
                  return {
                    ...card,
                    level,
                    current_exp: currentExp,
                    exp_to_level: expToLevel
                  };
                }
                return card;
              });
            });

            if (currentCard.courseId) {
              setTimeout(() => {
                updateCourseLevel(currentCard.courseId);
              }, 1000);
            }
          } catch (expError) {
            console.error('Error updating card leveling:', expError);
            setFeedback('Great job! Keep up the streak!');
          }
        } else {
          const srsFields = buildSrsExperienceFields(
            committedSrsThisCheck ? srsResultForUi : null
          );
          if (srsFields.newlyMastered) {
            setExperienceData(srsFields);
            setShowExperiencePopup(true);
            setFeedback('');
          } else {
            const nextReviewSource = committedSrsThisCheck && cardProgressResult?.nextReview
              ? cardProgressResult
              : committedSrsThisCheck && srsResultForUi?.nextReview
                ? srsResultForUi
                : null;
            setFeedback(
              formatCheckRecallFeedback({
                wasExactMatch,
                reviewMetrics: srsPayload,
                scheduleProgress: nextReviewSource,
                srsSchedulingPaused
              })
            );
          }
        }

        if ((committedSrsThisCheck || recordedOrientationThisCheck) && cardProgressResult) {
          setCards((prev) =>
            prev.map((card) =>
              card.id === currentCard.id
                ? mergeCardSrsProgress(card, cardProgressResult)
                : card
            )
          );
        }
      }

      setIsCorrect(wasExactMatch);

      try {
        await loadStats();
      } catch (statsError) {
        console.error('Error loading stats:', statsError);
      }
    } catch (error) {
      console.error('Error in handleAnswer:', error);
      setError('Failed to update progress');
    }
  };

  // Save line progress for line-by-line mode
  const saveLineProgress = async (snippetId, lineIndex) => {
    try {
      // Find the current card to check if it's a temporary course card
      const currentCard = cards[currentCardIndex];
      // Only skip saves if this is a temporary course card (no deck AND has course metadata)
      const isTemporaryCourseCard = !deck && currentCard && (currentCard.courseId || currentCard.courseTitle);

      if (isTemporaryCourseCard) {
        console.log(`Temporary course card ${snippetId} - skipping line progress save to Firestore`);
        if (updateCardProgress) {
          updateCardProgress(snippetId, lineIndex);
        }
        return;
      }

      if (!deck) {
        if (updateCardProgress) {
          updateCardProgress(snippetId, lineIndex);
        }
        return;
      }

      const deckId = resolveStudyCardDeckId(deck, currentCard);
      if (!deckId) {
        if (updateCardProgress) {
          updateCardProgress(snippetId, lineIndex);
        }
        return;
      }
      const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${snippetId}`);

      await updateDoc(snippetRef, { currentLineIndex: lineIndex });

      if (updateCardProgress) {
        updateCardProgress(snippetId, lineIndex);
      }

      console.log(`Saved progress for ${deck ? 'card' : 'snippet'} ${snippetId}: line ${lineIndex}`);
    } catch (error) {
      console.error(`Error saving line progress for ${deck ? 'card' : 'snippet'} ${snippetId}:`, error);
    }
  };

  // Handle completion for line-by-line mode
  const handleCompletion = async (results) => {
    console.log("Handling completion...");
    setIsComplete(true);

    const allCorrectResults = new Array(lines.length).fill(true);
    setLineResults(allCorrectResults);

    const correctCount = lines.length - initiallyWrongLines.length;
    const accuracy = lines.length > 0 ? correctCount / lines.length : 0;
    const accuracyPct = Math.round(accuracy * 100);
    const success = accuracy >= 0.7;

    let completionMessage = `Challenge complete! Accuracy: ${accuracyPct}%`;
    setFeedback(completionMessage);

    try {
      const currentCard = cards[currentCardIndex];

      if (deck) {
        try {
          const deckId = typeof deck === 'string' ? deck : (deck.deckId || deck.id);
          const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${currentCard.id}`);

          await updateDoc(snippetRef, {
            accuracy: accuracyPct,
            correctLines: correctCount,
            totalLines: lines.length,
            lastCompleted: new Date().toISOString()
          });
        } catch (error) {
          console.error(`Error saving accuracy and correct lines for card ${currentCard.id}:`, error);
        }

        try {
          const deckId = typeof deck === 'string' ? deck : (deck.deckId || deck.id);
          const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${currentCard.id}`);
          await updateDoc(snippetRef, { currentLineIndex: 0 });
        } catch (error) {
          console.error(`Error directly saving line progress for card ${currentCard.id}:`, error);
        }
      }

      if (!deck) {
        completionMessage = success
          ? `Challenge complete! Accuracy: ${accuracyPct}%`
          : `Keep practicing to improve! Accuracy: ${accuracyPct}%`;
        setFeedback(completionMessage);
      } else {
        // Line-by-line is learn-only practice — never grade or reschedule SRS.
        try {
          const deckId = typeof deck === 'string' ? deck : (deck.deckId || deck.id);
          await levelingSystem.updateCookbookLevel(deckId);
        } catch (error) {
          console.error('Error updating cookbook level:', error);
        }

        completionMessage = success
          ? `Challenge complete! Accuracy: ${accuracyPct}%`
          : `Keep practicing to improve! Accuracy: ${accuracyPct}%`;

        setFeedback(completionMessage);

        const popupData = {
          snippetExp: 0,
          snippetLeveledUp: false,
          accuracy: accuracyPct,
          correctLines: correctCount,
          totalLines: lines.length,
          nextReviewText: ''
        };
        setExperienceData(popupData);
        setShowExperiencePopup(true);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    }

    console.log("Completion handling finished. isComplete:", isComplete);

    if (deck) {
      try {
        const deckId = typeof deck === 'string' ? deck : (deck.deckId || deck.id);
        const snippetRef = doc(db, `users/${userId}/decks/${deckId}/cards/${currentCard.id}`);

        updateDoc(snippetRef, { initiallyWrongLines: [] })
          .then(() => {
            console.log(`Reset wrong lines for card ${currentCard.id} for next session`);
          })
          .catch(error => {
            console.error(`Error resetting wrong lines for card ${currentCard.id}:`, error);
          });
      } catch (error) {
        console.error('Error preparing to reset wrong lines for next session:', error);
      }
    }
  };

  // AI explanation method
  // isPreviewSide: optional parameter to indicate if we're on the preview side (viewing solution)
  // forceFullSnippet: when true, always explain the entire `currentCard.solution`
  //   regardless of `studyMode`. Lets Learn Mode users ask for a whole-snippet
  //   read while previewing the full code (instead of getting a single-line
  //   explanation generated for whatever line happened to be active).
  const handleExplain = async (isPreviewSide = false, forceFullSnippet = false) => {
    if (!cards.length) return;

    if (disableAiFeatures) {
      setFeedback('Create a free account to use AI explanations.');
      setIsExplaining(false);
      setFlashcardViewMode('study');
      return;
    }

    // Check if user is premium using combined check
    if (!isPremiumUser) {
      setFeedback('AI explanations are a premium feature. Please upgrade to access this functionality.');
      setFlashcardViewMode('study');
      // Redirect to subscription page
      if (setView) {
        setView('subscription');
      }
      return;
    }

    setIsExplaining(true);
    setExplanation('');

    try {
      const currentCard = cards[currentCardIndex];
      const language = currentCard.language || deck?.language || 'JavaScript';

      if (studyMode === 'lineByLine' && !forceFullSnippet) {
        // For line-by-line mode, explain the current line
        if (lines.length === 0 || currentLineIndex >= lines.length) {
          setFeedback('No line to explain. Please start the challenge first.');
          setIsExplaining(false);
          setFlashcardViewMode('study');
          return;
        }

        const currentLine = lines[currentLineIndex];
        if (!currentLine) {
          setFeedback('No line to explain. Please start the challenge first.');
          setIsExplaining(false);
          setFlashcardViewMode('study');
          return;
        }

        // Get surrounding lines for context (if available)
        const surroundingLines = [];

        // Add previous line if available
        if (currentLineIndex > 0) {
          surroundingLines.push(lines[currentLineIndex - 1]);
        }

        // Add current line
        surroundingLines.push(currentLine);

        // Add next line if available
        if (currentLineIndex < lines.length - 1) {
          surroundingLines.push(lines[currentLineIndex + 1]);
        }

        // Join surrounding lines for context
        const context = surroundingLines.join('\n');

        // Call the AI to explain the line, passing the premium status
        const explanation = await explainLine(currentLine, language, context, isPremiumUser);

        // Store the code being explained
        setExplanationCode(currentLine);
        setExplanationType('line');

        // Update the state with the explanation
        setExplanation(explanation);

        setFlashcardViewMode('explanation');

        // Clear any existing feedback to avoid confusion
        setFeedback('');
      } else {
        // For regular mode, explain the current snippet
        // Use solution if: on preview side, showAnswer is true, full-snippet
        // mode forced (Learn-Mode preview), or userCode is empty/not available.
        // Otherwise use userCode
        let snippet;
        if (
          isPreviewSide ||
          showAnswer ||
          forceFullSnippet ||
          !userCode ||
          !userCode.trim()
        ) {
          // Explain the solution code
          snippet = currentCard.solution;
        } else {
          // Explain the user's code
          snippet = userCode;
        }

        if (!snippet || !snippet.trim()) {
          setFeedback('No code available to explain. Please ensure the card has a solution.');
          setIsExplaining(false);
          setFlashcardViewMode('study');
          return;
        }

        // Call the AI to explain the snippet, passing the premium status
        let explanation = await explainSnippet(snippet, language, currentCard.title, isPremiumUser);

        console.log('AI explanation received:', explanation);
        console.log('Explanation type:', typeof explanation);
        console.log('Explanation length:', explanation?.length);

        // Check if explanation is valid
        if (!explanation || typeof explanation !== 'string' || explanation.trim() === '') {
          console.error('Empty or invalid explanation received from AI');
          setFeedback('Failed to get explanation. The AI service returned an empty response.');
          setIsExplaining(false);
          setFlashcardViewMode('study');
          return;
        }

        // Process the explanation to convert markdown to HTML
        // First, preserve code blocks before processing
        const codeBlockPlaceholder = '___CODE_BLOCK_PLACEHOLDER___';
        const codeBlocks = [];
        let codeBlockIndex = 0;

        // Replace code blocks with placeholders
        explanation = explanation.replace(/```[\s\S]*?```/g, (match) => {
          codeBlocks.push(match);
          return `${codeBlockPlaceholder}${codeBlockIndex++}${codeBlockPlaceholder}`;
        });

        // Remove markdown headers (# Header) and replace with bold text
        explanation = explanation.replace(/^#+\s+(.+)$/gm, '<strong>$1</strong>');

        // Remove markdown bold formatting (**text**)
        explanation = explanation.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

        // Remove numbered headings with bold (1. **Overview**)
        explanation = explanation.replace(/^(\d+)\.\s+\*\*([^*]+)\*\*/gm, '<strong>$1. $2</strong>');

        // Convert list markers to bullets
        explanation = explanation.replace(/^\s*[-*]\s+/gm, '• ');

        // Restore code blocks
        codeBlocks.forEach((codeBlock, index) => {
          explanation = explanation.replace(
            `${codeBlockPlaceholder}${index}${codeBlockPlaceholder}`,
            `<pre><code>${codeBlock.replace(/```[\w]*\n?/g, '').replace(/```/g, '')}</code></pre>`
          );
        });

        console.log('Processed explanation:', explanation);
        console.log('Processed explanation length:', explanation.length);

        // Store the code being explained and its type
        setExplanationCode(snippet);
        setExplanationType(
          isPreviewSide || showAnswer || forceFullSnippet || !userCode || !userCode.trim()
            ? 'solution'
            : 'user'
        );

        // Update the state with the processed explanation
        setExplanation(explanation);

        setFlashcardViewMode('explanation');

        // Clear any existing feedback to avoid confusion
        if (feedback && !isCorrect) {
          setFeedback('');
        }
      }
    } catch (err) {
      console.error('Error explaining snippet:', err);

      // Show more detailed error message to help with debugging
      const errorMessage = err.message || 'Failed to get explanation. Please try again.';

      // If it's a specific error about authentication or service, provide more context
      if (errorMessage.includes('logged in') || errorMessage.includes('authentication')) {
        setFeedback('You must be logged in to use AI features. Please log in and try again.');
      } else if (errorMessage.includes('premium') || errorMessage.includes('subscription')) {
        setFeedback('AI explanations are a premium feature. Please upgrade to access this functionality.');
        if (setView) {
          setView('subscription');
        }
      } else if (errorMessage.includes('quota') || errorMessage.includes('limit')) {
        setFeedback('AI service quota exceeded. Please try again later or upgrade your plan.');
      } else {
        setFeedback(`Failed to get explanation: ${errorMessage}`);
      }
    } finally {
      setIsExplaining(false);
    }
  };

  // Editor change handlers
  const handleEditorChange = (newCode) => {
    // After a prior submit, `showAnswer` stays true until reset. The reworked
    // Study UI keeps the editor editable, so clear the flag when the user
    // starts a new attempt — otherwise the next Check only resets and marks wrong.
    if (showAnswer) {
      setShowAnswer(false);
    }
    if (!hasStartedCoding && newCode.trim()) {
      setHasStartedCoding(true);
    }
    setUserCode(newCode);
  };

  const handleEditorChangeLineByLine = (newCode) => {
    // If user hasn't started coding yet and is typing something, mark as started
    if (!hasStartedCoding && newCode.trim()) {
      setHasStartedCoding(true);
    }

    setUserCode(newCode);

    // Auto check functionality for line-by-line mode
    if (newCode.trim()) {
      try {
        if (checkTimeout) {
          clearTimeout(checkTimeout);
        }

        if (showLine) {
          return;
        }

        const currentLine = lines[currentLineIndex];
        const isExactMatch = newCode === currentLine;

        const currentCard = cards[currentCardIndex];
        const language = currentCard.language || deck?.language || 'JavaScript';
        const normalizedUserCode = normalizeCode(newCode, language);
        const normalizedSolution = normalizeCode(currentLine, language);
        const isNormalizedMatch = normalizedUserCode === normalizedSolution;

        console.log('Auto check comparison:', {
          userCode: newCode,
          currentLine,
          isExactMatch,
          normalizedUserCode,
          normalizedSolution,
          isNormalizedMatch,
          currentLineIndex,
          totalLines: lines.length,
          isLastLine: currentLineIndex === lines.length - 1
        });

        if (isExactMatch || isNormalizedMatch) {
          // Show all green for correct answer
          setLineComparison({
            segments: [{ text: newCode || currentLine, isCorrect: true, isMissing: false, startIndex: 0 }],
            userText: newCode,
            solutionText: currentLine
          });

          const timeout = setTimeout(() => {
            setFeedback(' Correct! Next line...');
            setIsCorrect(true);

            setLineResults(prevResults => {
              const newResults = [...prevResults];
              newResults[currentLineIndex] = true;
              return newResults;
            });

            if (currentLineIndex === lines.length - 1) {
              console.log('Auto check: Last line correct, handling completion');
              handleCompletion();
            } else {
              const nextLineIndex = currentLineIndex + 1;
              setCurrentLineIndex(nextLineIndex);
              saveLineProgress(currentCard.id, nextLineIndex);
              resetStateForNewLine();
              setLineComparison(null); // Clear comparison when moving to next line
            }
          }, 300);

          setCheckTimeout(timeout);
        }
      } catch (error) {
        console.error('Error in auto check:', error);
      }
    }
  };

  // Submit handlers
  const handleSubmit = () => {
    console.log('handleSubmit called', { showAnswer, userCode: userCode.trim(), hasStartedCoding });

    if (showAnswer) {
      if (userCode.trim() && hasStartedCoding) {
        checkCodeSolution();
        return;
      }
      resetState();
      return;
    } else if (!userCode.trim() && !hasStartedCoding) {
      // In test mode, never reveal solution without typing; require the user to attempt an answer
      if (isTestMode) {
        setFeedback('Please type your answer before checking.');
        return;
      }
      setShowAnswer(true);
    } else {
      console.log('Calling checkCodeSolution...');
      checkCodeSolution();
    }
  };

  const handleSubmitLineByLine = () => {
    console.log('handleSubmitLineByLine called', {
      currentLineIndex,
      linesLength: lines.length,
      showLine,
      userCode: userCode.trim(),
      hasStartedCoding
    });

    // Validate we have lines available
    if (!lines || lines.length === 0) {
      console.error('handleSubmitLineByLine: No lines available');
      setError('No lines available. Please refresh the page.');
      return;
    }

    // Handle completion case for last line when showing solution
    if (currentLineIndex === lines.length - 1 && showLine) {
      handleCompletion();
      return;
    }

    // Handle retry case - user wants to try again after seeing the solution
    if (showLine) {
      resetStateForRetry();
      return;
    }

    // Handle empty code cases
    if (!userCode.trim()) {
      if (!hasStartedCoding) {
        // User hasn't started coding yet, show the solution
        setShowLine(true);
        setFeedback('Showing the solution line. Click "Try Again" to continue.');
      } else {
        // User has started but deleted everything
        setFeedback('Please enter some code before checking.');
      }
      return;
    }

    // All validations passed, proceed with checking the line
    console.log('Calling checkLine...');
    checkLine();
  };

  // Retry handler for line-by-line mode
  const handleRetry = () => {
    setCurrentLineIndex(0);
    resetStateForNewLine();
    setLineResults(new Array(lines.length).fill(null));
    setIsComplete(false);

    if (cards.length > 0) {
      const currentCard = cards[currentCardIndex];
      saveLineProgress(currentCard.id, 0);
    }
  };

  const resetStateForRetry = () => {
    setHasStartedCoding(false);
    setShowLine(false);
    setFeedback('');
  };

  // Fetch card data for line-by-line mode
  const fetchCardData = useCallback(async (card) => {
    // Check if this is a temporary course card (from CourseViewer) that doesn't exist in Firestore
    // Deck cards with course metadata should still be fetched from Firestore
    const isTemporaryCourseCard = !deck && (card.courseId || card.courseTitle);

    if (isTemporaryCourseCard) {
      console.log(`Temporary course card ${card.id} - skipping Firestore fetch, using card data directly`);
      return card;
    }

    if (!deck) {
      return card;
    }
    const deckId = typeof deck === 'string' ? deck : (deck.deckId || deck.id);
    const cardRef = doc(db, `users/${userId}/decks/${deckId}/cards/${card.id}`);

    const cardDoc = await getDoc(cardRef);
    if (cardDoc.exists()) {
      return { ...card, ...cardDoc.data() };
    }
    return card;
  }, [deck, userId]);

  // Initialize card for line-by-line mode
  const initializeCard = async () => {
    if (cards.length > 0) {
      const currentCard = cards[currentCardIndex];
      console.log('initializeCard: Processing card for line-by-line mode:', {
        id: currentCard.id,
        title: currentCard.title,
        hasLevel: currentCard.level !== undefined,
        isCourseCard: !!(currentCard.courseId || currentCard.courseTitle)
      });

      let processedCard = currentCard;
      if (!deck && currentCard.level === undefined) {
        try {
          processedCard = await initializeSnippetLeveling(currentCard.id, currentCard);
        } catch (error) {
          console.error('Error initializing leveling fields:', error);
        }
      }

      try {
        const updatedCard = await fetchCardData(processedCard);
        const language = updatedCard.language || deck?.language || 'JavaScript';

        console.log('initializeCard: Updated card data:', {
          id: updatedCard.id,
          language,
          solutionLength: updatedCard.solution?.length || 0
        });

        // First use the synchronous version to get a default set of lines
        const defaultCodeLines = splitCodeIntoLines(updatedCard.solution, language);
        console.log('initializeCard: Default code lines:', defaultCodeLines);
        setLines(defaultCodeLines);
        setLineResults(new Array(defaultCodeLines.length).fill(null));
        setVariableHints(extractVariableHintsFromLines(defaultCodeLines, language));

        try {
          // Then use the async utility function to get user-specific patterns
          const codeLines = await splitCodeIntoLinesAsync(updatedCard.solution, language, userId);
          console.log('initializeCard: Final code lines:', codeLines);
          setLines(codeLines);
          setLineResults(new Array(codeLines.length).fill(null));
          setVariableHints(extractVariableHintsFromLines(codeLines, language));
        } catch (error) {
          console.error('Error splitting code into lines:', error);
        }

        // Extract comment hints from raw solution for right-side hints panel
        const hints = extractCommentHints(updatedCard.solution, language);
        setCommentHints(hints);

        // Load saved wrong lines if they exist, otherwise initialize empty array
        if (updatedCard.initiallyWrongLines) {
          setInitiallyWrongLines(updatedCard.initiallyWrongLines);
        } else {
          setInitiallyWrongLines([]);
        }

        // Reset completion state when loading a new card
        setIsComplete(false);

        // Load saved progress if it exists
        if (updatedCard.currentLineIndex !== undefined &&
          updatedCard.currentLineIndex > 0 &&
          updatedCard.currentLineIndex < defaultCodeLines.length) {
          setCurrentLineIndex(updatedCard.currentLineIndex);
        } else {
          setCurrentLineIndex(0);
        }

        resetStateForNewLine();
      } catch (error) {
        console.error('Error fetching latest card data:', error);
        setError('Failed to load card data. Please try again.');
      }
    }
  };

  // Back button handler
  const handleBack = () => {
    if (setView) {
      setView(previousView || 'home');
    }
  };

  // Dismiss test completion summary and navigate back to roadmap (or previous view)
  const dismissTestCompleteAndGoBack = () => {
    setTestCompleteSummary(null);
    if (typeof setView === 'function') {
      setView(previousView || 'home');
    }
  };

  // Return the logic interface
  return {
    // State
    cards,
    loading,
    currentCardIndex,
    error,
    studyMode,
    setStudyMode,

    // Regular study mode state
    showAnswer,
    setShowAnswer,
    userCode,
    setUserCode,
    feedback,
    setFeedback,
    hasStartedCoding,
    setHasStartedCoding,
    isCorrect,
    setIsCorrect,

    // Line-by-line study mode state
    currentLineIndex,
    setCurrentLineIndex,
    lineResults,
    setLineResults,
    initiallyWrongLines,
    setInitiallyWrongLines,
    isComplete,
    setIsComplete,
    lines,
    setLines,
    showLine,
    setShowLine,
    checkTimeout,
    setCheckTimeout,
    lineComparison,
    setLineComparison,
    commentHints,
    variableHints,
    setCommentHints,

    // Notes state
    notes,
    setNotes,
    isSavingNotes,
    setIsSavingNotes,

    // AI explanation state
    explanation,
    setExplanation,
    isExplaining,
    setIsExplaining,
    flashcardViewMode,
    setFlashcardViewMode,
    explanationCode,
    explanationType,

    // Experience popup state
    showExperiencePopup,
    setShowExperiencePopup,
    experienceData,
    setExperienceData,

    // Stats and leveling
    stats,
    setStats,
    srs,
    levelingSystem,

    // Refs
    editorContainerRef,
    editorHeight,
    setEditorHeight,

    // Data management methods
    initializeSnippetLeveling,
    loadDeckAndCards,
    loadStats,
    loadNotes,
    saveNotesDebounced,
    handleNotesChange,
    updateDailyProgress,
    updateCourseLevel,

    // Navigation methods
    resetState,
    resetEditorForRetry,
    resetStateForNewLine,
    handleNext,
    handlePrevious,
    updateCardProgress,

    // Validation methods
    analyzeCode,
    checkCodeSolution,
    checkLine,

    // Experience methods
    handleAnswer,
    handleSrsAgain,
    handleCompletion,
    saveLineProgress,

    // AI explanation methods
    handleExplain,

    // Editor methods
    handleEditorChange,
    handleEditorChangeLineByLine,

    // Submit methods
    handleSubmit,
    handleSubmitLineByLine,

    // Line-by-line specific methods
    handleRetry,
    resetStateForRetry,

    // Navigation methods
    navigateToCard,
    fetchCardData,
    initializeCard,

    // Navigation methods
    handleBack,

    // Test mode: which cards have been answered (for disabling back / glossary)
    testModeSnippetResults,

    // Test mode: show one summary when test is complete (all snippets done) before going back
    testCompleteSummary,
    dismissTestCompleteAndGoBack
  };
};

export default useStudyLogic;

